import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Fragment, ReactNode } from "react";
import Button from "./Button";

interface ModalProps {
  header?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  isOpen?: boolean;
  isCloseButtonVisible?: boolean;
  onCloseModal: () => void;
  size?: "sm" | "md" | "lg" | "xl";
  backgroundColor?: string;
  height?: string;
  maxHeight?: string;
  titleCentered?: boolean;
  position?: "center" | "bottom";
}

const sizeClasses: Record<NonNullable<ModalProps["size"]>, string> = {
  xl: "w-11/12 max-w-6xl",
  lg: "w-9/12 min-w-96 max-w-4xl",
  md: "w-4/12 min-w-72 max-w-xl",
  sm: "w-80",
};

export default function Modal({
  header,
  title,
  subtitle,
  children,
  footer,
  isOpen = true,
  size = "md",
  isCloseButtonVisible = true,
  backgroundColor = "bg-white",
  onCloseModal,
  height = "80vh",
  maxHeight = "100vh",
  titleCentered = false,
  position = "center",
}: ModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={`fixed inset-0 z-[60] flex ${
          position === "bottom"
            ? "items-end justify-center pb-20"
            : "items-center justify-center"
        }`}
        onClose={() => {}}
        static
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel
            className={`${sizeClasses[size]} transform overflow-hidden rounded-md ${backgroundColor} p-2 md:p-6 text-left align-middle shadow-xl transition-all flex flex-col relative z-[70]`}
            style={{ height, maxHeight }}
          >
            <div className="flex flex-row justify-between mb-2">
              <div className={titleCentered ? "flex-1 text-center" : ""}>
                {header}
                <DialogTitle
                  as="h3"
                  className={titleCentered ? "text-center" : ""}
                >
                  {title}
                </DialogTitle>
              </div>
              {isCloseButtonVisible && onCloseModal && (
                <Button variant="alternative" size="sm" onClick={onCloseModal}>
                  <XMarkIcon color="black" className="h-5 w-5" />
                </Button>
              )}
            </div>
            {subtitle ? (
              <div className="mt-2">
                <div className="text-xs text-gray-500">{subtitle}</div>
              </div>
            ) : (
              <div />
            )}
            <div className="flex-1 min-h-0 overflow-y-auto px-0 md:px-4 lg:px-8 flex flex-col">
              {children}
            </div>
            {footer ? (
              <div className="mt-auto pt-4">
                <hr className="text-base-gray-80 my-1" />
                <div className="mt-4">{footer}</div>
              </div>
            ) : (
              <div />
            )}
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}
